import React from 'react';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';
import { Icon } from '@packages/ui/shared';
import { Typography } from '@packages/ui/shared';
import colors from '@packages/core/styles/colors';
import { StepConfig } from './stepper';

const useStyles = createUseStyles({
    outerContainer: {
        paddingLeft: 80,
        paddingRight: 80,
    },
    innerContainer: {
        flex: 1,
    },
    line: {
        backgroundColor: colors.grayThree,
        flex: 1,
        height: 2,
        left: 0,
        position: 'absolute',
        right: 0,
        top: 7,
        zIndex: 0,
    },
    lineProgress: {
        backgroundColor: colors.blueOne,
        height: 2,
        left: 0,
        position: 'absolute',
        top: 7,
        transition: 'width 0.4s ease',
        zIndex: 1,
    },
    lineEraser: {
        backgroundColor: '#fff',
        height: 2,
        pointerEvents: 'none',
        position: 'absolute',
        right: 0,
        top: 7,
        width: '50%',
        zIndex: 2,
    },
    stepContainer: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        zIndex: 2,
        '&:hover': {
            cursor: 'pointer',
        },
    },
    stepContent: {
        width: 16,
        height: 16,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
    },
    stepCompleted: {
        backgroundColor: colors.blueOne,
        color: '#fff',
    },
    stepActive: {
        backgroundColor: '#fff',
        border: `4px solid ${colors.blueOne}`,
        color: colors.blueOne,
    },
    stepInactive: {
        backgroundColor: colors.grayThree,
        color: '#fff',
        border: `4px solid #fff`,
    },
    required: {
        backgroundColor: '#fff',
    },
});

const LINE_PROGRESS_START = 50;
const LINE_PROGRESS_STEP = 100;

export function getStepIndicatorStyle(
    classes: Record<string, string>,
    isActive: boolean,
    isComplete: boolean,
    isInvalid: boolean
): { indicatorClass: string; indicatorIcon: React.ReactNode } {
    let indicatorClass = classes.stepContent;
    let indicatorIcon: React.ReactNode = null;

    if (isComplete) {
        indicatorClass += ` ${classes.stepCompleted}`;
        indicatorIcon = <Icon size={12} name="checkmark" color="white" />;
    } else if (isInvalid || (isInvalid && isActive)) {
        indicatorClass += ` ${classes.required}`;
        indicatorIcon = <Icon size={21} name="warning" color="redOne" />;
    } else if (isActive) {
        indicatorClass += ` ${classes.stepActive}`;
    } else {
        indicatorClass += ` ${classes.stepInactive}`;
    }

    return { indicatorClass, indicatorIcon };
}

interface StepperHeaderProps {
    steps: StepConfig[];
    currentStepIndex: number;
    stepStatuses: Record<string, { isInvalid: boolean; isComplete: boolean }>;
    goToStep: (index: number) => void;
}

export function StepperHeader({ steps, currentStepIndex, stepStatuses, goToStep }: StepperHeaderProps) {
    const { t } = useTranslation();
    const classes = useStyles();
    const stepLineProgress = [LINE_PROGRESS_START + currentStepIndex * LINE_PROGRESS_STEP];

    return (
        <div className={`${classes.outerContainer} d-flex align-items-center justify-content-center flex-shrink-0`}>
            {steps.map((step, index) => {
                const isActive = index === currentStepIndex;
                const stepStatus = stepStatuses[step.id];
                const isComplete = step.isComplete(stepStatus);
                const isInvalid = step.isInvalid(stepStatus);
                const { indicatorClass, indicatorIcon } = getStepIndicatorStyle(
                    classes,
                    isActive,
                    isComplete,
                    isInvalid
                );
                const labelColor = isInvalid ? 'redOne' : isActive ? 'blueOne' : 'grayFive';

                return (
                    <div
                        key={index}
                        className={`${classes.innerContainer} d-flex align-items-center justify-content-center position-relative`}
                    >
                        <div className={classes.line} />
                        <div className={classes.lineProgress} style={{ width: `${stepLineProgress[index]}%` }} />
                        {isComplete && <div className={classes.lineProgress} />}
                        {index === steps.length - 1 && <div className={classes.lineEraser} />}
                        <div className={classes.stepContainer} onClick={() => goToStep(index)}>
                            <div className={indicatorClass}>{indicatorIcon}</div>
                            <div>
                                <Typography variant="caption" color={labelColor}>
                                    {t(step.label)}
                                </Typography>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
